import React, { Fragment, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Loader from 'components/Loader';
import { withOrderUtils } from 'utils/withOrderUtils';
import { dataLayerPush } from 'utils/tracking';
import { Drawer, SuccessBlock } from 'components/Ui';
import { selectDeliveryOrder } from 'containers/PrimoAccount/selectors';
import moment from 'moment';
import DeliveryOrderForm from './DeliveryOrderForm';
import SkipDeliveryOrderForm from './SkipDeliveryOrderForm';
import { cancelDeliveryOrderEditing, skipDeliveryOrder } from '../actions';
import {
  selectIsEditingDeliveryOrder,
  selectDeliveryOrderIsFetching,
  selectServiceLocationIsSuccessUpdatedDeliveryOrder,
  selectServiceLocationIsSuccessSkippedDeliveryOrder,
} from '../selectors';
import doStyles from './delivery-order-styles.scss';

const EditDeliveryOrderDrawer = ({
  updateDeliveryOrder,
  onRefresh,
  onRemoveHold,
  onCancelEditSkipDelivery,
}) => {
  const dispatch = useDispatch();
  const [showSkipDeliveryOrderForm, changeShowSkipDeliveryOrderForm] = useState(
    false,
  );
  const open = useSelector(selectIsEditingDeliveryOrder());
  const isSuccessUpdated = useSelector(
    selectServiceLocationIsSuccessUpdatedDeliveryOrder(),
  );
  const isSuccessSkipped = useSelector(
    selectServiceLocationIsSuccessSkippedDeliveryOrder(),
  );
  const isLoading = useSelector(selectDeliveryOrderIsFetching());
  const deliveryOrder = useSelector(selectDeliveryOrder());

  const handleSubmit = (changedDelivery) => {
    updateDeliveryOrder(deliveryOrder, changedDelivery);
  };

  const handleCloseDrawer = () => {
    dispatch(cancelDeliveryOrderEditing());
    setTimeout(() => {
      changeShowSkipDeliveryOrderForm(false);
      onRefresh();
    }, 100);
  };

  const handleShowSkipDeliveryOrder = () => {
    changeShowSkipDeliveryOrderForm(true);
  };

  const handleSubmitSkipDelivery = () => {
    dataLayerPush(
      'SelfServe',
      {
        event: 'skip-future-delivery',
        date: moment(deliveryOrder.date).format('YYYY-MM-DD'),
      },
      'dlS18',
      'PUSH_SKIP_DELIVERY_ORDER_SUCCESS',
    );
    dispatch(skipDeliveryOrder(deliveryOrder.date));
  };

  const handleCancelEditSkipDelivery = () => {
    onCancelEditSkipDelivery(deliveryOrder);
  };

  const handleRemoveHold = () => {
    onRemoveHold(deliveryOrder);
  };

  const handleCloseSkipDelivery = () => {
    changeShowSkipDeliveryOrderForm(false);
  };

  const renderContent = () => {
    if (isSuccessUpdated) {
      return (
        <div className={doStyles.successMessage}>
          <SuccessBlock text="Your updates have been received." />
        </div>
      );
    }

    if (isSuccessSkipped) {
      return (
        <div className={doStyles.successMessage}>
          <SuccessBlock
            text={['Okay, we will skip this delivery -', 'see you next time!']}
          />
        </div>
      );
    }

    if (showSkipDeliveryOrderForm) {
      return (
        <SkipDeliveryOrderForm
          deliveryOrder={deliveryOrder}
          onSubmit={handleSubmitSkipDelivery}
          onClose={handleCloseSkipDelivery}
        />
      );
    }

    return (
      <DeliveryOrderForm
        deliveryOrder={deliveryOrder}
        onSubmit={handleSubmit}
        onClose={handleCloseDrawer}
        onShowSkipDeliveryOrder={handleShowSkipDeliveryOrder}
        onRemoveHold={handleRemoveHold}
        onCancelEditSkipDelivery={handleCancelEditSkipDelivery}
      />
    );
  };

  return (
    <Fragment>
      <Drawer
        headerTitle={
          showSkipDeliveryOrderForm || isSuccessSkipped
            ? 'Skip Delivery'
            : 'View or edit your order'
        }
        data-tracking={
          showSkipDeliveryOrderForm || isSuccessSkipped
            ? 'Skip Delivery'
            : 'edit scheduled order'
        }
        onClose={handleCloseDrawer}
        open={open}
        noMargins
      >
        <Loader loading={isLoading} local>
          {renderContent()}
        </Loader>
      </Drawer>
    </Fragment>
  );
};

export default withOrderUtils(EditDeliveryOrderDrawer);
